import React from 'react';
import { Deviation } from '../../services/setpointsApi';
import { formatDeviation } from '../../utils/deviation';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Reading {
    key: string;
    label: string;
    unit: string;
    value: number | string | null;
    warn_above?: number | null;
    warn_below?: number | null;
}

export interface DeviceTile {
    type: 'device';
    device_ieee: string;
    label: string;
    online: boolean | null;
    last_reading_at?: string | null;
    readings: Reading[];
}

export type Tile = DeviceTile;

// ── Utility functions ─────────────────────────────────────────────────────────

export function formatValue(value: number | string | null): string {
    if (value === null || value === undefined) return '—';
    const num = typeof value === 'number' ? value : Number(value);
    if (!isNaN(num)) {
        return Number.isInteger(num) ? String(num) : num.toFixed(1);
    }
    return String(value);
}

export function isWarning(r: Reading): boolean {
    const num = typeof r.value === 'number' ? r.value : Number(r.value);
    if (isNaN(num)) return false;
    if (r.warn_above != null && num > r.warn_above) return true;
    if (r.warn_below != null && num < r.warn_below) return true;
    return false;
}

// ── Tile Components ───────────────────────────────────────────────────────────

interface TileCardProps {
    tile: DeviceTile;
    dark?: boolean;
    onReadingClick?: (deviceIeee: string, deviceLabel: string, property: string, propertyLabel: string) => void;
    /** Deviations keyed by `${ieee}::${parameter_key}` (24 h window). */
    deviations?: Record<string, Deviation>;
}

export const DeviceTileCard: React.FC<TileCardProps> = ({ tile, dark = false, onReadingClick, deviations }) => {
    // Avstängda enheter skickas inte från servern, så här finns bara påslagna.
    const bgColor = dark
        ? (tile.online === true ? '#1e293b' : tile.online === false ? '#2d1f1f' : '#27272a')
        : (tile.online === true ? '#ffffff' : tile.online === false ? '#fef2f2' : '#f4f4f5');
    const borderColor = dark
        ? (tile.online === true ? '#334155' : tile.online === false ? '#7f1d1d' : '#52525b')
        : (tile.online === true ? '#e2e8f0' : tile.online === false ? '#fca5a5' : '#d4d4d8');
    const textColor = dark ? '#e2e8f0' : '#1a202c';
    const statusColor = tile.online === true
        ? (dark ? '#4ade80' : '#10b981')
        : tile.online === false
            ? (dark ? '#f87171' : '#ef4444')
            : (dark ? '#9ca3af' : '#71717a');
    const mutedColor = dark ? '#64748b' : '#6b7280';

    return (
        <div style={{
            background: bgColor,
            border: `2px solid ${borderColor}`,
            borderRadius: 12,
            padding: 'clamp(16px, 4vw, 24px) clamp(18px, 4vw, 28px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
        }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 'clamp(16px, 4vw, 20px)', fontWeight: 700, color: textColor, letterSpacing: 0.3 }}>
                        {tile.label}
                    </span>
                    {tile.last_reading_at && (
                        <span style={{ fontSize: 11, color: mutedColor }}>
                            {new Date(tile.last_reading_at).toLocaleString('sv-SE', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </span>
                    )}
                </div>
                <span style={{
                    fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase',
                    color: statusColor,
                    background: dark
                        ? (tile.online === true ? 'rgba(74,222,128,.12)' : tile.online === false ? 'rgba(248,113,113,.12)' : 'rgba(156,163,175,.12)')
                        : (tile.online === true ? 'rgba(16,185,129,.12)' : tile.online === false ? 'rgba(239,68,68,.12)' : 'rgba(113,113,122,.12)'),
                    padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
                }}>
                    {tile.online === true ? '● uppkopplad' : tile.online === false ? '● frånkopplad' : '○ okänt'}
                </span>
            </div>

            {/* Readings */}
            {tile.readings.length === 0 && (
                <span style={{ color: mutedColor, fontSize: 13 }}>Inga parametrar konfigurerade</span>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 110px), 1fr))', gap: 10 }}>
                {tile.readings.map(r => {
                    const warn = isWarning(r);
                    const warnBg = dark ? 'rgba(248,113,113,.12)' : 'rgba(239,68,68,.08)';
                    const warnBorder = dark ? 'rgba(248,113,113,.4)' : 'rgba(239,68,68,.3)';
                    const warnText = dark ? '#fca5a5' : '#dc2626';
                    const normalBg = dark ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.02)';
                    const labelColor = warn ? warnText : mutedColor;
                    const valueColor = warn ? warnText : textColor;

                    const dev = deviations?.[`${tile.device_ieee}::${r.key}`];

                    return (
                        <div key={r.key} style={{
                            background: warn ? warnBg : normalBg,
                            border: warn ? `1px solid ${warnBorder}` : '1px solid transparent',
                            borderRadius: 8,
                            padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
                            cursor: onReadingClick ? 'pointer' : 'default',
                            transition: 'transform .15s, box-shadow .15s',
                        }}
                        onClick={() => onReadingClick?.(tile.device_ieee, tile.label, r.key, r.label)}
                        onMouseEnter={(e) => {
                            if (onReadingClick) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = dark
                                    ? '0 4px 12px rgba(0,0,0,.4)'
                                    : '0 4px 12px rgba(0,0,0,.15)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (onReadingClick) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }
                        }}
                        >
                            <div style={{ color: labelColor, fontSize: 11, marginBottom: 4 }}>{r.label}</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 'clamp(28px, 8vw, 36px)', fontWeight: 700, color: valueColor, lineHeight: 1 }}>
                                    {formatValue(r.value)}
                                </span>
                                {r.unit && (
                                    <span style={{ fontSize: 'clamp(14px, 3.5vw, 16px)', color: valueColor, fontWeight: 500 }}>
                                        {r.unit}
                                    </span>
                                )}
                            </div>
                            {warn && (
                                <div style={{ fontSize: 11, color: warnText, marginTop: 4, fontWeight: 600 }}>⚠ Varning</div>
                            )}
                            {/* Avvikelse mot börvärde (24 h). n/a om inget börvärde är satt. */}
                            <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px dashed ${dark ? 'rgba(255,255,255,.12)' : 'rgba(0,0,0,.08)'}` }}>
                                <div style={{ fontSize: 11, color: mutedColor }}>
                                    Avvikelse: <span style={{ fontWeight: 700, color: dev ? valueColor : mutedColor }}>
                                        {dev ? formatDeviation(dev.devAvg, dev.devNull) : 'n/a'}
                                    </span>
                                </div>
                                {dev && (
                                    <div style={{ fontSize: 10, color: mutedColor, marginTop: 2 }}>
                                        Snitt över 24 h (bör {dev.target_value}{dev.unit ? ` ${dev.unit}` : ''})
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
